/* eslint react-hooks/exhaustive-deps: "warn" */
import Link from 'next/link';

import { useInt } from 'hooks/useInt';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './FddFields.module.scss';

type Translate = (key: string) => string;

const getFieldGroups = (t: Translate) => [
  {
    category: t('Income statement'),
    fields: [
      t('Turnover'),
      t('Cost of sales'),
      t('Gross profit'),
      t('Operating profit'),
      t('EBITDA'),
      t('Pre-tax profit'),
      t('Taxation'),
      t('Profit after tax'),
      t('Retained profits'),
    ],
    special: false,
  },
  {
    category: t('Balance sheet'),
    fields: [
      t('Total assets'),
      t('Fixed assets'),
      t('Tangible assets'),
      t('Intangible assets'),
      t('Stock'),
      t('Trade debtors'),
      t('Cash'),
      t('Current assets'),
      t('Trade creditors'),
      t('Current liabilities'),
      t('Total liabilities'),
      t('Net assets'),
      t('Working capital'),
      t('Shareholder funds'),
      t('Capital employed'),
      t('P&L account reserve'),
      t('Revaluation reserve'),
      t('Sundry reserves'),
    ],
    special: false,
  },
  {
    category: t('Margins and returns'),
    fields: [
      t('Gross margin'),
      t('Operating profit margin'),
      t('EBITDA margin'),
      t('Pre-tax profit margin'),
      t('Net margin'),
      t('Return on assets'),
      t('Return on capital employed'),
      t('Return on equity'),
    ],
    special: false,
  },
  {
    category: t('Liquidity and gearing'),
    fields: [
      t('Current ratio'),
      t('Liquidity ratio'),
      t('Cash to current liabilities'),
      t('Cash to total assets'),
      t('Cash to turnover'),
      t('Inventory turnover'),
      t('Gearing liability'),
      t('Gearing gross debt'),
      t('Gearing net debt'),
      t('Debt to capital'),
    ],
    special: false,
  },
  {
    category: t('On the filing itself'),
    fields: [
      t('Latest accounts date filed'),
      t('Reporting period'),
      t('Consolidated or company only'),
      t('Currency'),
      t('Ticker where listed'),
    ],
    special: false,
  },
  {
    category: t('Where a company files little'),
    fields: [t('Estimated revenue'), t('Estimated employee numbers')],
    special: true,
    tag: t('Labelled as an estimate'),
  },
];

export const FddFields = () => {
  const { t } = useInt();
  const fieldGroups = getFieldGroups(t);

  return (
    <Section background="#f8f9fd" className={styles.root}>
      <div className={styles.head}>
        <TitleBlock
          title={t('Every line the filing carries, mapped to one model.')}
          description={t(
            'These are the financial fields in our data dictionary. The same field means the same thing whether the accounts were filed in Manchester, Munich or Milan, and each one arrives with the filing it was read from and the date it was last updated.',
          )}
          align="left"
        />
      </div>

      <div className={styles.grid}>
        {fieldGroups.map((group) => (
          <div key={group.category} className={`${styles.groupRow} ${group.special ? styles.groupRowSpecial : ''}`}>
            <div className={styles.groupCategory}>
              {group.category}
              {group.special && group.tag && <span className={styles.estimateTag}>{group.tag}</span>}
            </div>
            <div className={styles.groupFields}>
              {group.fields.map((field) => (
                <span key={field} className={styles.field}>
                  {field}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className={styles.footer}>
        <Link href="/data-dictionary" className={styles.dictLink}>
          {t('See the full data dictionary →')}
        </Link>
      </div>
    </Section>
  );
};
